import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "../../../../api/axios";
import { 
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from "@heroicons/react/24/outline";

const INVOICES_PAGE_SIZE = 50;

const emptyTotals = () => ({
  invoiced_ex_vat: 0,
  paid_ex_vat: 0,
  pending_ex_vat: 0,
  overdue_ex_vat: 0,
});

export const InvoicesTab = ({ project, canEdit = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!project?.id) return;
    setPage(1);
    setInvoices([]);
    setMeta(null);
  }, [project?.id]);

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    const append = page > 1;
    (async () => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        const response = await axios.get(
          `/projects/${project.id}/invoices?page=${page}&per_page=${INVOICES_PAGE_SIZE}`
        );
        if (cancelled) return;
        if (response.data.success) {
          const rows = response.data.data || [];
          setInvoices((prev) => (append ? [...prev, ...rows] : rows));
          setMeta(response.data.meta || null);
          setError(null);
        } else {
          setError("Failed to load invoices");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching invoices:", err);
          setError("Failed to load invoices");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project?.id, page]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "pending":
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case "overdue":
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <DocumentTextIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getInvoiceStatus = (invoice) => {
    const isPaid = invoice.is_paid || (invoice.paid_amount && invoice.amount && invoice.paid_amount >= invoice.amount);
    if (isPaid) {
      return "paid";
    }
    if (invoice.invoiceDueDate) {
      const dueDate = new Date(invoice.invoiceDueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDate < today) {
        return "overdue";
      }
    }
    return "pending";
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "paid":
        return t("project.invoices.status.paid") || "Betalt";
      case "pending":
        return t("project.invoices.status.pending") || "Venter";
      case "overdue":
        return t("project.invoices.status.overdue") || "Forfalt";
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK'
    }).format(amount);
  };

  const totals = meta?.totals || emptyTotals();
  const totalCount = meta?.total_count ?? invoices.length;
  const hasMore =
    Boolean(meta) && invoices.length > 0 && invoices.length < totalCount;

  if (loading && !invoices.length && !error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("project.invoices.error.title") || "Feil ved lasting"}
        </h3>
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.invoices.overview")}
        </h3>
        {canEdit ? (
        <button
          type="button"
          disabled
          title={t("project.invoices.create_disabled_hint")}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 opacity-50 cursor-not-allowed focus:outline-none"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t("project.invoices.create")}
        </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.invoices.totals.invoiced_ex_vat")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(totals.invoiced_ex_vat || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.invoices.status.paid")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(totals.paid_ex_vat || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.invoices.status.pending")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(totals.pending_ex_vat || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.invoices.status.overdue")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(totals.overdue_ex_vat || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {invoices.length > 0 ? (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fakturanummer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Beskrivelse
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("project.invoices.amount")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("project.invoices.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("project.invoices.due_date")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opprettet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => {
                  const status = getInvoiceStatus(invoice);
                  return (
                    <tr key={invoice.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {invoice.invoice_number || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.invoiceComment || invoice.comment || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(parseFloat(invoice.amount) || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                          {getStatusIcon(status)}
                          <span className="ml-1">{getStatusLabel(status)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.invoiceDueDate ? new Date(invoice.invoiceDueDate).toLocaleDateString('no-NO') : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('no-NO') : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => navigate(`/dashboard/customer-invoices/${invoice.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Vis faktura"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hasMore ? (
            <div className="flex flex-col items-center gap-2 px-4 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {loadingMore ? (
                  <>
                    <span className="inline-block h-4 w-4 mr-2 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                    {t("project.invoices.pagination.loading_more")}
                  </>
                ) : (
                  t("project.invoices.pagination.load_more")
                )}
              </button>
              <span className="text-xs text-gray-500">
                {t("project.invoices.pagination.loaded_of_total", {
                  loaded: invoices.length,
                  total: totalCount,
                })}
              </span>
            </div>
          ) : invoices.length > 0 && totalCount > 0 ? (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-center text-xs text-gray-500">
              {t("project.invoices.pagination.all_loaded", { total: totalCount })}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("project.invoices.no_invoices.title") || "Ingen fakturaer"}
            </h3>
            <p className="text-gray-500 mb-4">
              {t("project.invoices.no_invoices.description") || "Ingen fakturaer er opprettet for dette prosjektet ennå"}
            </p>
            {canEdit ? (
            <button
              type="button"
              disabled
              title={t("project.invoices.create_disabled_hint")}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 opacity-50 cursor-not-allowed"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t("project.invoices.create")}
            </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
